// nakamaAuth.js — Service d'authentification & temps réel Nakama (SDK @heroiclabs/nakama-js).
// Gère : session persistée (localStorage), auth e-mail / invité / Google / Facebook,
// compte (pseudo/avatar), amis, notifications, socket temps réel (chat + notifs).
// Tolérant aux pannes : renvoie des valeurs neutres si Nakama est indisponible/désactivé.
import { Client } from '@heroiclabs/nakama-js';
import { nakama } from '../config/nakama';

const SS = { token: 'vg_nk_token', refresh: 'vg_nk_refresh' };

function parseUrl(url) {
  try {
    const u = new URL(url);
    return { host: u.hostname, port: u.port || (u.protocol === 'https:' ? '443' : '80'), ssl: u.protocol === 'https:' };
  } catch { return null; }
}
function deviceId() {
  const k = 'vg_appstore_device';
  let id = null; try { id = localStorage.getItem(k); } catch { /* */ }
  if (!id) { id = 'as-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9); try { localStorage.setItem(k, id); } catch { /* */ } }
  return id;
}
// Pseudo invité STABLE par appareil, format uniforme : « joueur_123456 » (minuscule + underscore).
function guestName() {
  const k = 'vg_guest_name';
  let n = null; try { n = localStorage.getItem(k); } catch { /* */ }
  // Migre les anciens noms (« Invité #1234 », « joueur_num_123 ») vers le format « joueur_123456 ».
  if (!n || !/^joueur_\d{4,}$/.test(n)) {
    n = 'joueur_' + (100000 + Math.floor(Math.random() * 900000));
    try { localStorage.setItem(k, n); } catch { /* */ }
  }
  return n;
}

// Normalise un pseudo en identifiant uniforme : minuscules, accents retirés, tout ce qui n'est pas
// [a-z0-9] devient « _ », underscores repliés. Ex : « VertinGo Website » → « vertingo_website ».
export function normalizeUsername(name) {
  const base = String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')   // enlève les accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  return base || null;
}

// Extrait le champ « name » d'un ID token Google (JWT) sans dépendance externe.
function jwtName(token) {
  try {
    const p = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return p.name || p.given_name || (p.email ? p.email.split('@')[0] : null);
  } catch { return null; }
}

class NakamaAuth {
  constructor() {
    this.client = null;
    this.session = null;
    this.socket = null;
    this.cfg = parseUrl(nakama.url);
    this._listeners = new Set();       // abonnés au changement d'état (auth)
    this._notifCbs = new Set();        // callbacks notifications temps réel
    this._msgCbs = new Set();          // callbacks messages de chat
    this._statusCbs = new Set();       // callbacks statut/présence des amis (en ligne/hors ligne)
    this._reconnectCbs = new Set();    // callbacks appelés après une RECONNEXION du socket (rejoin canaux)
    this._chanPresCbs = new Set();     // callbacks présence de CANAL de chat (join/leave d'un salon/groupe)
    this._lifecycleBound = false;      // listeners pageshow/visibilité posés une seule fois
  }

  get enabled() { return nakama.enabled && !!this.cfg; }

  _client() {
    if (!this.client && this.cfg) this.client = new Client(nakama.key, this.cfg.host, this.cfg.port, this.cfg.ssl);
    return this.client;
  }

  onChange(cb) { this._listeners.add(cb); return () => this._listeners.delete(cb); }
  _emit() { this._listeners.forEach((cb) => { try { cb(this.session); } catch { /* */ } }); }

  _persist(session) {
    this.session = session;
    try {
      if (session) { localStorage.setItem(SS.token, session.token); localStorage.setItem(SS.refresh, session.refresh_token || ''); }
      else { localStorage.removeItem(SS.token); localStorage.removeItem(SS.refresh); }
    } catch { /* */ }
    this._emit();
  }

  // Restaure une session existante (au démarrage), avec rafraîchissement si expirée.
  async restore() {
    // Session invité locale persistée (Nakama indispo) → on la restaure telle quelle.
    try {
      if (localStorage.getItem('vg_local_guest') === '1' && !localStorage.getItem(SS.token)) {
        return this._guestLocal(localStorage.getItem('vg_local_guest_name') || undefined);
      }
    } catch { /* */ }
    if (!this.enabled) return null;
    let token = null, refresh = null;
    try { token = localStorage.getItem(SS.token); refresh = localStorage.getItem(SS.refresh); } catch { /* */ }
    if (!token) return null;
    try {
      const { Session } = await import('@heroiclabs/nakama-js');
      let session = Session.restore(token, refresh || '');
      if (session.isexpired(Date.now() / 1000)) {
        if (!refresh) { this._persist(null); return null; }
        session = await this._client().sessionRefresh(session);
      }
      this._persist(session);
      this._openSocket();
      return session;
    } catch { this._persist(null); return null; }
  }

  async _after(session) {
    this._persist(session); await this._openSocket();
    // Nouvelle inscription (compte créé côté Nakama) → déclenche l'email de bienvenue (best-effort).
    if (session && session.created && !session._localGuest) this._sendWelcome(session);
    return session;
  }
  // Demande au backend d'envoyer l'email de bienvenue (il récupère l'email réel via le token Nakama).
  _sendWelcome(session) {
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      if (!base || !session.token) return;
      fetch(`${base}/account/welcome`, { method: 'POST', headers: { Authorization: `Bearer ${session.token}` } }).catch(() => {});
    } catch { /* best-effort */ }
  }

  // Détecte un username auto-généré par Nakama (ex « jMOTYcGOpW » : 8-12 alphanum, avec majuscules,
  // sans underscore) → on peut le migrer vers le pseudo normalisé du joueur.
  _looksRandom(u) {
    return !!u && /^[A-Za-z0-9]{8,12}$/.test(u) && /[A-Z]/.test(u) && !u.includes('_');
  }
  _usernameTaken(e) {
    const m = String((e && (e.message || e.statusText)) || '').toLowerCase();
    return (e && e.status === 409) || m.includes('username') || m.includes('already');
  }
  // Suffixe numérique à 6 chiffres → identifiant unique et lisible (« vertingo_website_483920 »).
  _suffixed(base) { return `${base}_${100000 + Math.floor(Math.random() * 900000)}`; }
  // Authentifie avec username = pseudo normalisé + suffixe numérique TOUJOURS (identifiant de joueur
  // unique et lisible). fn(username) = un des authenticate* du SDK. baseName = nom (« VertinGo Website »).
  async _authUnique(fn, baseName) {
    const base = normalizeUsername(baseName);
    if (!base) return this._after(await fn(undefined));
    const tries = [this._suffixed(base), this._suffixed(base), this._suffixed(base)];
    let session = null;
    for (let i = 0; i < tries.length && !session; i++) {
      try { session = await fn(tries[i]); }
      catch (e) {
        if (!this._usernameTaken(e)) { // compte existant (username ignoré) OU autre erreur → repli sans username
          try { session = await fn(undefined); } catch (e2) { throw e2; }
        }
        // sinon collision de création → on tente le suffixe suivant
      }
    }
    if (!session) session = await fn(undefined);
    await this._after(session);
    await this._ensureUsername(baseName);  // migre un ancien compte au username aléatoire
    await this._normalizeDisplayName();    // pseudo SANS espaces (Google/FB importent « Prénom Nom »)
    return this.session;
  }
  // Aligne le nom affiché sur le pseudo : pas d'espaces (Nakama importe « Vertin Go Website » depuis Google).
  async _normalizeDisplayName() {
    if (!this.session || this.session._localGuest) return;
    try {
      const acc = await this._client().getAccount(this.session);
      const dn = (acc && acc.user && acc.user.display_name) || '';
      if (dn && !/\s/.test(dn)) return;   // déjà sans espace → on n'y touche pas
      const clean = normalizeUsername(dn) || this.session.username;
      if (clean && clean !== dn) await this.updateAccount({ displayName: clean });
    } catch { /* best-effort */ }
  }
  // Un username est « final » (bon format) s'il finit par un suffixe numérique (_123456) ou est un
  // pseudo invité (joueur_num_...). Sinon (aléatoire Nakama OU pseudo propre SANS suffixe) → on migre
  // vers « base_XXXXXX » pour un identifiant de joueur uniforme et unique.
  _looksFinal(u) { return /_\d{4,}$/.test(u || '') || /^joueur_\d+$/.test(u || ''); }
  async _ensureUsername(baseName) {
    const base = normalizeUsername(baseName);
    if (!base || !this.session || this.session._localGuest) return;
    if (this._looksFinal(this.session.username)) return;   // déjà au bon format → on ne touche pas
    for (let i = 0; i < 3; i++) {
      const u = this._suffixed(base);
      if (await this.updateAccount({ username: u })) { try { this.session.username = u; } catch { /* */ } return; }
    }
  }
  // Invité : si le username est aléatoire (device déjà connu de Nakama), le remet au format joueur_XXXXXX.
  async _ensureGuestName(name) {
    if (!this.session || this.session._localGuest) return;
    if (this._looksFinal(this.session.username)) return;   // déjà joueur_XXXXXX / base_XXXXXX → intact
    if (await this.updateAccount({ username: name })) { try { this.session.username = name; } catch { /* */ } }
  }

  async authEmail(email, password, create, username) {
    if (create) return this._authUnique((u) => this._client().authenticateEmail(email, password, true, u), username || email.split('@')[0]);
    const s = await this._client().authenticateEmail(email, password, false, undefined);
    return this._after(s);
  }
  async authGuest(username) {
    const name = username || guestName();   // « Invité #1234 » stable par appareil (pas juste « Invité »)
    // 1) Vraie session invité Nakama si le serveur répond…
    try {
      if (this.enabled) {
        const s = await this._client().authenticateDevice(deviceId(), true, name);
        await this._after(s);
        await this._ensureGuestName(name);   // device déjà existant → Nakama a ignoré le username → on migre
        return this.session;
      }
    } catch { /* Nakama indisponible → repli local ci-dessous */ }
    // 2) …sinon repli invité PUREMENT LOCAL : l'utilisateur accède à l'app sans
    //    créer de compte ; les fonctions en ligne (classements, chat) se dégradent en douceur.
    return this._guestLocal(name);
  }
  _guestLocal(username) {
    const name = username || guestName();
    const guest = { token: 'local-guest', user_id: 'guest-' + deviceId(), username: name, _localGuest: true, isexpired: () => false };
    try { localStorage.setItem('vg_local_guest', '1'); localStorage.setItem('vg_local_guest_name', name); } catch { /* */ }
    this.session = guest; this._emit();
    return guest;
  }
  async authGoogle(googleToken, name) {
    // Le nom vient du profil (param) ou, à défaut, du champ « name » de l'ID token Google.
    return this._authUnique((u) => this._client().authenticateGoogle(googleToken, true, u), name || jwtName(googleToken));
  }
  async authFacebook(fbToken, name) {
    return this._authUnique((u) => this._client().authenticateFacebook(fbToken, true, u), name);
  }

  // ── Conversion INVITÉ → vrai compte : on LIE une identité au compte device COURANT (même user_id →
  // l'XP et la progression sont CONSERVÉES). error:'taken' = l'identité appartient déjà à un autre compte.
  async linkEmail(email, password) {
    if (!this.session || this.session._localGuest) return { ok: false, error: 'no-session' };
    try { await this._client().linkEmail(this.session, { email, password }); return { ok: true }; }
    catch (e) { return { ok: false, error: (e && e.status === 409) ? 'taken' : 'fail' }; }
  }
  async linkGoogle(token) {
    if (!this.session || this.session._localGuest) return { ok: false, error: 'no-session' };
    try { await this._client().linkGoogle(this.session, { token }); return { ok: true }; }
    catch (e) { return { ok: false, error: (e && e.status === 409) ? 'taken' : 'fail' }; }
  }
  async linkFacebook(token) {
    if (!this.session || this.session._localGuest) return { ok: false, error: 'no-session' };
    try { await this._client().linkFacebook(this.session, { token }); return { ok: true }; }
    catch (e) { return { ok: false, error: (e && e.status === 409) ? 'taken' : 'fail' }; }
  }

  async logout() {
    try { if (this.socket) this.socket.disconnect(true); } catch { /* */ }
    this.socket = null;
    try { localStorage.removeItem('vg_local_guest'); localStorage.removeItem('vg_local_guest_name'); } catch { /* */ }
    this._persist(null);
  }

  isLoggedIn() { return !!this.session; }

  async getAccount() {
    if (!this.session) return null;
    if (this.session._localGuest) {
      return { user: { id: this.session.user_id, username: this.session.username, display_name: this.session.username } };
    }
    try { return await this._client().getAccount(this.session); } catch { return null; }
  }
  async updateAccount({ username, displayName, avatarUrl }) {
    if (!this.session) return false;
    try {
      await this._client().updateAccount(this.session, {
        username: username || undefined,
        display_name: displayName || undefined,
        avatar_url: avatarUrl || undefined,
      });
      return true;
    } catch { return false; }
  }

  // ── Amis ──
  async listFriends(state) { // state: 0 amis, 1 demande envoyée, 2 demande reçue, 3 bloqué (undefined = tous)
    if (!this.session) return [];
    try { const r = await this._client().listFriends(this.session, state, 50); return r.friends || []; } catch { return []; }
  }
  async addFriendByUsername(username) {
    if (!this.session || !username) return false;
    try { await this._client().addFriends(this.session, [], [username]); return true; } catch { return false; }
  }
  async removeFriend(userId) {
    if (!this.session) return false;
    try { await this._client().deleteFriends(this.session, [userId]); return true; } catch { return false; }
  }
  // Accepter une demande REÇUE : complétion d'une amitié déjà sollicitée → jamais bloqué par whoFriend.
  async acceptFriend(userId) {
    if (!this.session) return false;
    try { await this._client().addFriends(this.session, [userId], []); return true; } catch { return false; }
  }
  // NOUVELLE demande d'ami : passe par le serveur (vg_friend_request) qui applique whoFriend.
  // Renvoie { ok, reason } (reason = message si refusé par la confidentialité du destinataire).
  async addFriendById(userId) {
    if (!this.session || !userId) return { ok: false };
    if (this.session._localGuest) {
      try { await this._client().addFriends(this.session, [userId], []); return { ok: true }; } catch { return { ok: false }; }
    }
    try {
      const r = await this.rpc('vg_friend_request', { to: userId });
      const d = typeof r === 'string' ? JSON.parse(r) : (r || {});
      if (d.ok) return { ok: true };
      // Refus PAR CONFIDENTIALITÉ du destinataire → on respecte (pas de repli).
      if (d.error === 'refuse') return { ok: false, reason: d.reason };
      // Échec TECHNIQUE du RPC (ex: nk.friends_add) → repli sur l'API REST du SDK, qui fonctionne.
      await this._client().addFriends(this.session, [userId], []);
      return { ok: true };
    } catch {
      // Repli SDK si le RPC est indisponible (module non chargé) → ne bloque pas la fonctionnalité.
      try { await this._client().addFriends(this.session, [userId], []); return { ok: true }; } catch { return { ok: false }; }
    }
  }
  // Progression XP / niveaux (autoritative Nakama, module vg_progression). getXp() lit le profil
  // unifié (mêmes chiffres que l'in-game), addXp() ajoute de l'XP d'action (plafonnée serveur).
  async getXp() {
    if (!this.session || this.session._localGuest) return null;
    try {
      const r = await this.rpc('vg_progress_get', {});
      const d = typeof r === 'string' ? JSON.parse(r) : (r || null);
      if (!d || !d.ok || !d.progression) return null;
      const p = d.progression;
      return { ok: true, xp: p.xp, level: p.level, status: p.status, xpForCurrent: p.xpForCurrent, xpForNext: p.xpForNext, unlocks: p.unlocks };
    } catch { return null; }
  }
  // Progression PUBLIQUE d'un autre joueur (pour la carte profil) → l'objet progression complet
  // (xp, level, status, matches, wins, losses, draws, perGame, objectives, unlocks) ou null.
  async getUserProgress(userId) {
    if (!this.session || !userId) return null;
    try {
      const r = await this.rpc('vg_progress_get', { user_id: userId });
      const d = typeof r === 'string' ? JSON.parse(r) : (r || null);
      return d && d.progression ? d.progression : null;
    } catch { return null; }
  }
  async addXp(amount, reason) {
    if (!this.session || this.session._localGuest) return null;
    try { const r = await this.rpc('vg_xp_add', { amount, reason: reason || '' }); return typeof r === 'string' ? JSON.parse(r) : (r || null); } catch { return null; }
  }
  // Recherche de joueurs par préfixe de pseudo (autocomplétion) → [{ user_id, username, display_name, avatar_url }].
  async searchUsers(query) {
    const q = String(query || '').trim();
    if (!this.session || this.session._localGuest || q.length < 2) return [];
    try {
      // rpc() renvoie DÉJÀ le payload parsé (objet), ou une chaîne dans certains cas → on gère les deux.
      const r = await this.rpc('vg_user_search', { q });
      const d = typeof r === 'string' ? JSON.parse(r) : (r || {});
      return Array.isArray(d.users) ? d.users : [];
    } catch { return []; }
  }
  // Résout un membre par son pseudo exact → { user_id, username } ou null.
  async getUserByUsername(username) {
    if (!this.session || !username) return null;
    try {
      const r = await this._client().getUsers(this.session, [], [username], []);
      return (r.users && r.users[0]) || null;
    } catch { return null; }
  }

  // ── Notifications ──
  // Nom déterministe d'un canal room ÉPHÉMÈRE (non-persistant) pour la signalisation d'un DM
  // (typing / accusé de lecture / réactions). Passer par un room non-persistant évite la
  // notification DM native de Nakama (« X wants to chat ») générée à CHAQUE message persisté.
  dmSignalName(otherId) {
    const me = (this.session && this.session.user_id) || '';
    const pair = [me, otherId || ''].sort().map((x) => String(x).replace(/-/g, '').slice(0, 12)).join('');
    return 'dmsig' + pair;   // ≤ ~30 chars, stable pour la paire
  }

  async listNotifications(limit = 50) {
    if (!this.session || this.session._localGuest) return [];
    // FETCH REST DIRECT (contourne un BUG du client nakama-js : `readArrayBufferAsText` fait `new Array(view.length)`
    // et jette « Invalid array length » sur certaines réponses → toute la liste échouait, onglet notifs VIDE alors
    // qu'on avait plein de notifs). Le REST renvoie du JSON propre parsé nativement. + refresh session si 401.
    const cfg = this.cfg || {};
    const base = (cfg.ssl ? 'https' : 'http') + '://' + cfg.host + ':' + cfg.port;
    const _fetch = async () => {
      const resp = await fetch(base + '/v2/notification?limit=' + limit, { headers: { Authorization: 'Bearer ' + this.session.token } });
      if (!resp.ok) { const err = new Error('HTTP ' + resp.status); err.status = resp.status; throw err; }
      const j = await resp.json();
      return this._dedupNotifications(j.notifications || []);
    };
    try {
      if (this.session.isexpired && this.session.isexpired(Date.now() / 1000) && this.session.refresh_token) {
        try { this._persist(await this._client().sessionRefresh(this.session)); } catch (e) { /* retry ci-dessous */ }
      }
      return await _fetch();
    } catch (e) {
      try {
        if (this.session && this.session.refresh_token && e && e.status === 401) {
          this._persist(await this._client().sessionRefresh(this.session));
          return await _fetch();
        }
      } catch (e2) { try { console.warn('[notif] refresh+retry KO:', (e2 && (e2.status || e2.message)) || e2); } catch (_) { /* */ } }
      try { console.warn('[notif] listNotifications échec:', (e && (e.status || e.message)) || e); } catch (_) { /* */ }
      return [];
    }
  }
  // ANTI-SPAM générique : regroupe les notifications par (code + type + expéditeur) et ne garde que
  // la PLUS RÉCENTE de chaque groupe, avec un compteur (_dupCount) et la liste des doublons (_dupIds)
  // pour purge. Corrige l'inondation « X wants to chat » et plafonne TOUT type répétitif.
  _dedupNotifications(list) {
    const groups = new Map(); const order = [];
    for (const n of list) {
      const c = n.content || {};
      const sender = n.sender_id || c.sender_id || c.from || c.user_id || c.groupId || c.group_id || '';
      const type = c.type || '';
      const key = `${n.code}|${type}|${sender || n.subject || ''}`;
      const cur = groups.get(key);
      if (!cur) { n._dupCount = 1; n._dupIds = []; groups.set(key, n); order.push(key); continue; }
      const nNew = (n.create_time || '') >= (cur.create_time || '');
      const keep = nNew ? n : cur; const drop = nNew ? cur : n;
      keep._dupCount = (cur._dupCount || 1) + 1;
      // BUG CORRIGÉ : avant, quand la notif entrante était plus récente (keep=n, drop=cur), on faisait
      // concat(cur._dupIds, …, drop._dupIds) où drop._dupIds === cur._dupIds → le tableau était concaténé DEUX FOIS
      // et DOUBLAIT à chaque fusion (croissance exponentielle → 2^26 → RangeError "Invalid array length" → onglet
      // notifs vide). On utilise désormais keep/drop explicitement (le survivant absorbe l'id + les doublons du drop).
      keep._dupIds = [].concat(keep._dupIds || [], [drop.id], drop._dupIds || []);
      groups.set(key, keep);
    }
    return order.map((k) => groups.get(k));
  }
  // Suivi "vu" du badge : Nakama conserve les notifs → on mémorise l'instant de dernière consultation
  // (par utilisateur, en localStorage) et le badge ne compte QUE les notifs plus récentes. Consulter
  // l'onglet → markNotificationsSeen() → le badge se vide et RESTE vide au refresh.
  _seenKey() { return 'vg_notif_seen_' + ((this.session && this.session.user_id) || 'anon'); }
  _seenTs() { try { return parseInt(localStorage.getItem(this._seenKey()) || '0', 10) || 0; } catch { return 0; } }
  markNotificationsSeen() { try { localStorage.setItem(this._seenKey(), String(Date.now())); } catch { /* */ } }
  async unreadNotifCount() {
    const list = await this.listNotifications();
    const seen = this._seenTs();
    return list.filter((n) => { const t = n.create_time ? new Date(n.create_time).getTime() : 0; return t > seen; }).length;
  }
  // Supprime une notif du serveur (ex : après avoir accepté/refusé une demande d'ami) → l'inbox reste propre.
  async deleteNotification(id) {
    if (!this.session || !id) return false;
    try { await this._client().deleteNotifications(this.session, [id]); return true; } catch { return false; }
  }
  // Suppression EN LOT (purge des doublons collapsés, bouton « Tout effacer »). Batch de 100 (limite API).
  async deleteNotifications(ids) {
    if (!this.session || !ids || !ids.length) return false;
    const uniq = [...new Set(ids.filter(Boolean))];
    try { for (let i = 0; i < uniq.length; i += 100) await this._client().deleteNotifications(this.session, uniq.slice(i, i + 100)); return true; } catch { return false; }
  }
  // Récupère des profils par user_id (pseudo/avatar) → utilisé pour afficher pseudo+avatar dans les chats
  // (les messages ne portent que le username brut, souvent aléatoire). Renvoie [{ id, username, display_name, avatar_url }].
  async getUsers(ids) {
    if (!this.session || !ids || !ids.length) return [];
    try { const r = await this._client().getUsers(this.session, ids, [], []); return (r && r.users) || []; } catch { return []; }
  }
  onNotification(cb) { this._notifCbs.add(cb); return () => this._notifCbs.delete(cb); }
  // Présence de CANAL (join/leave d'un salon de groupe/communauté) — multi-abonnés (Set), filtrer par channel_id.
  onChannelPresence(cb) { this._chanPresCbs.add(cb); return () => this._chanPresCbs.delete(cb); }

  // ── Socket temps réel (notifications + chat) ──
  async _openSocket() {
    if (!this.session || !this.cfg || this.socket) return;
    try {
      const socket = this._client().createSocket(this.cfg.ssl, false);
      await socket.connect(this.session, true);
      socket.onnotification = (n) => this._notifCbs.forEach((cb) => { try { cb(n); } catch { /* */ } });
      socket.onchannelmessage = (m) => this._msgCbs.forEach((cb) => { try { cb(m); } catch { /* */ } });
      socket.onstatuspresence = (ev) => this._statusCbs.forEach((cb) => { try { cb(ev); } catch { /* */ } });
      socket.onchannelpresence = (ev) => this._chanPresCbs.forEach((cb) => { try { cb(ev); } catch { /* */ } });
      // Coupure détectée (timeout d'inactivité proxy/Nakama, réseau, bfcache) → on oublie le socket mort ET on
      // RECONNECTE AUTOMATIQUEMENT (backoff). Avant, la reconnexion n'avait lieu qu'au retour d'onglet → un joueur
      // dont l'onglet restait ouvert et actif passait hors-ligne pour ses amis sans jamais revenir. Corrigé ici.
      socket.ondisconnect = () => { if (this.socket === socket) { this.socket = null; this._scheduleReconnect(); } };
      this.socket = socket;
      this._bindLifecycle();
      // Statut « en ligne » — indispensable pour que nos amis nous voient via followUsers.
      try { await socket.updateStatus(JSON.stringify({ a: 'En ligne' })); } catch { /* */ }
    } catch { /* */ }
  }
  // Reconnexion : rouvre le socket s'il est tombé, puis notifie les abonnés (les chats rejoignent leur canal).
  // force=true jette d'abord un socket potentiellement mort (retour de bfcache) pour repartir propre.
  async ensureSocket(force = false) {
    if (!this.session || this.session._localGuest || !this.enabled) return;
    if (force && this.socket) { try { this.socket.disconnect(false); } catch { /* */ } this.socket = null; }
    if (this.socket) return;
    await this._openSocket();
    if (this.socket) this._reconnectCbs.forEach((cb) => { try { cb(); } catch { /* */ } });
  }
  // Pose UNE fois les écouteurs de cycle de vie : au retour d'onglet (bfcache) ou de veille, on reconnecte
  // le socket temps réel (chat/présence) — corrige "les messages ne partent plus après un retour arrière".
  _bindLifecycle() {
    if (this._lifecycleBound || typeof window === 'undefined') return;
    this._lifecycleBound = true;
    window.addEventListener('pageshow', (e) => { if (e && e.persisted) this.ensureSocket(true); });
    window.addEventListener('online', () => this.ensureSocket(true));
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && !this.socket) this.ensureSocket(); });
    // CONTRÔLE DE SANTÉ : tant qu'on a une session, on garde le socket VIVANT même onglet ouvert+idle. S'il est
    // tombé → on rouvre ; s'il est là → on RAFRAÎCHIT le statut (keepalive qui empêche le timeout d'inactivité côté
    // proxy/Nakama et maintient notre présence « en ligne » pour les amis).
    this._healthIv = setInterval(() => {
      try {
        if (!this.session || this.session._localGuest || !this.enabled) return;
        if (!this.socket) { this.ensureSocket(); return; }
        if (typeof this._lastStatus === 'string') { this.socket.updateStatus(this._lastStatus).catch(() => {}); }   // keepalive + statut frais
      } catch { /* */ }
    }, 25000);
  }
  // Reconnexion AUTO avec backoff : le socket est tombé alors que la page est ouverte → on le rouvre tout seul.
  _scheduleReconnect(delay = 1500) {
    if (this._reconnTimer || !this.session || this.session._localGuest || !this.enabled) return;
    this._reconnTimer = setTimeout(async () => {
      this._reconnTimer = null;
      if (this.socket) return;
      await this.ensureSocket();
      if (!this.socket) this._scheduleReconnect(Math.min(15000, Math.round(delay * 1.7)));
    }, delay);
  }
  onReconnect(cb) { this._reconnectCbs.add(cb); return () => this._reconnectCbs.delete(cb); }
  onChatMessage(cb) { this._msgCbs.add(cb); return () => this._msgCbs.delete(cb); }

  // ── Groupes d'amis (Nakama groups) : créer / lister / inviter / quitter / membres ──
  // open:true → un ami peut REJOINDRE directement le groupe (sans invitation) depuis la page En ligne.
  async createGroup(name, description = '') {
    if (!this.session || this.session._localGuest || !name) return null;
    // Plafond de 16 membres/groupe (Nakama refuse l'adhésion au-delà pour un groupe ouvert). Choisi > à la
    // plus grosse partie (Méga bomberman = 12) pour laisser une marge (no-shows, rotation, spectateurs),
    // tout en gardant les groupes d'amis à taille humaine. Ajustable ici si besoin.
    try { return await this._client().createGroup(this.session, { name, description, open: true, max_count: 16 }); }
    catch { return null; }
  }
  // Rejoindre un groupe ouvert (groupe d'un ami). Groupe fermé → crée une demande d'adhésion.
  async joinGroup(groupId) {
    if (!this.session || this.session._localGuest || !groupId) return false;
    try { await this._client().joinGroup(this.session, groupId); return true; } catch { return false; }
  }
  // Bannir/exclure un membre d'un groupe — réservé au créateur/admin (superadmin/admin) par Nakama.
  async kickFromGroup(groupId, userId) {
    if (!this.session || !groupId || !userId) return false;
    try { await this._client().kickGroupUsers(this.session, groupId, [userId]); return true; } catch { return false; }
  }
  // Groupes AUXQUELS APPARTIENT un autre utilisateur (pour découvrir les groupes de mes amis à rejoindre).
  async listUserGroupsFor(userId) {
    if (!this.session || !userId) return [];
    try {
      const r = await this._client().listUserGroups(this.session, userId);
      return ((r && r.user_groups) || []).map((g) => (g.group ? { ...g.group, memberState: g.state } : null)).filter(Boolean);
    } catch { return []; }
  }
  async listMyGroups() {
    if (!this.session || this.session._localGuest) return [];
    try {
      const r = await this._client().listUserGroups(this.session, this.session.user_id);
      // On garde l'état (state 0=superadmin/créateur → peut supprimer) sur le groupe (champ myState).
      return ((r && r.user_groups) || []).map((g) => (g.group ? { ...g.group, myState: g.state } : null)).filter(Boolean);
    } catch { return []; }
  }
  async inviteToGroup(groupId, userIds) {
    if (!this.session || !groupId || !userIds || !userIds.length) return false;
    try { await this._client().addGroupUsers(this.session, groupId, userIds); return true; } catch { return false; }
  }
  async leaveGroup(groupId) {
    if (!this.session || !groupId) return false;
    try { await this._client().leaveGroup(this.session, groupId); return true; } catch { return false; }
  }
  // Supprime un groupe (réservé au créateur/superadmin par Nakama).
  async deleteGroup(groupId) {
    if (!this.session || !groupId) return false;
    try { await this._client().deleteGroup(this.session, groupId); return true; } catch { return false; }
  }
  async listGroupMembers(groupId) {
    if (!this.session || !groupId) return [];
    try {
      const r = await this._client().listGroupUsers(this.session, groupId);
      return ((r && r.group_users) || []).map((u) => u.user).filter(Boolean);
    } catch { return []; }
  }
  // Membres AVEC leur état (0=créateur/superadmin, 1=admin, 2=membre, 3=demande) → pour la gestion (bannir).
  async listGroupMembersFull(groupId) {
    if (!this.session || !groupId) return [];
    try {
      const r = await this._client().listGroupUsers(this.session, groupId);
      return ((r && r.group_users) || []).filter((u) => u.user).map((u) => ({ user: u.user, state: u.state }));
    } catch { return []; }
  }

  // Rejoint un canal (room=1, DM=2, groupe=3) ; persist=false pour un canal éphémère (ex: signaling vocal).
  // hidden=true → adhésion INVISIBLE : Nakama n'envoie PAS la notif de join (« X wants to chat ») à l'autre, et on
  // reçoit quand même les messages. Indispensable pour l'écoute PASSIVE (DMNotifier rejoint tous les DM au login).
  async joinChannel(target, type = 1, persist = true, hidden = false) {
    if (!this.socket) await this._openSocket();
    if (!this.socket) return null;
    try { return await this.socket.joinChat(target, type, persist, hidden); } catch { return null; }
  }
  async sendChat(channelId, message, extra = {}) {
    if (!channelId) return false;
    if (!this.socket) await this._openSocket();   // secours : socket tombé (bfcache/réseau) → on le rouvre
    if (!this.socket) return false;
    try { await this.socket.writeChatMessage(channelId, { message, ...extra }); return true; } catch { return false; }
  }
  // Historique récent d'un canal (messages persistés) → pour afficher le fil à l'ouverture du chat.
  async listChatHistory(channelId, limit = 30) {
    if (!channelId) return [];
    try {
      const r = await this._client().listChannelMessages(this.session, channelId, limit, false);
      // renvoyés du plus récent au plus ancien → on remet dans l'ordre chronologique
      return ((r && r.messages) || []).slice().reverse();
    } catch { return []; }
  }

  // ── RPC serveur (ex: événements cross-jeu vg_game_event) ──
  async rpc(id, payload) {
    if (!this.session) return null;
    try {
      // IMPORTANT : le SDK Nakama fait DÉJÀ JSON.stringify(input) à l'envoi ET JSON.parse au retour.
      // Il faut donc lui passer l'OBJET directement (pas une chaîne) : sinon il re-stringifie notre
      // chaîne → payload DOUBLE-encodé → côté Lua json_decode rend une string au lieu d'une table →
      // q="" (recherche vide) et game="generic" (salon invisible). Vérifié en direct via le SDK 2.8.0.
      const res = await this._client().rpc(this.session, id, payload || {});
      return res && res.payload !== undefined && res.payload !== null ? res.payload : null;
    } catch { return null; }
  }

  // ── Stockage cloud par utilisateur (favoris, wishlist, bibliothèque, succès, stats) ──
  // Repli localStorage pour l'invité local / Nakama indisponible → l'espace membre marche toujours.
  _lsKey(collection, key) { return 'vg_store_' + collection + '_' + key; }
  async storageWrite(collection, key, value, opts = {}) {
    const local = !this.session || this.session._localGuest;
    if (!local) {
      try {
        await this._client().writeStorageObjects(this.session, [{
          collection, key, value, permission_read: opts.publicRead ? 2 : 1, permission_write: 1,
        }]);
        return true;
      } catch { /* repli local ci-dessous */ }
    }
    try { localStorage.setItem(this._lsKey(collection, key), JSON.stringify(value)); } catch { /* */ }
    return true;
  }
  async storageRead(collection, key, fallback = null) {
    if (this.session && !this.session._localGuest) {
      try {
        const uid = this.session.user_id;
        const r = await this._client().readStorageObjects(this.session, { object_ids: [{ collection, key, user_id: uid }] });
        if (r && r.objects && r.objects.length) return r.objects[0].value;
      } catch { /* repli local */ }
    }
    try { const v = localStorage.getItem(this._lsKey(collection, key)); if (v != null) return JSON.parse(v); } catch { /* */ }
    return fallback;
  }

  // ── Classements (leaderboards Nakama) ──
  async leaderboardSubmit(leaderboardId, score, subscore = 0, metadata) {
    if (!this.session || this.session._localGuest) return null;
    try {
      const rec = await this._client().writeLeaderboardRecord(this.session, leaderboardId, { score: String(score), subscore: String(subscore), metadata });
      // Hook « grosse perf » : si le jeu marque le score comme notable (metadata.notable) OU nouveau record
      // perso battu largement → on l'annonce dans le fil d'actu (backend /news, kind perf). Best-effort.
      try {
        const notable = metadata && (metadata.notable || (rec && rec.rank && Number(rec.rank) === 1));
        if (notable) {
          const { reportPerf } = await import('./api');
          reportPerf({
            game: (metadata && metadata.game) || leaderboardId,
            player: (this.session && this.session.username) || 'Un joueur',
            score, text: (metadata && metadata.perfText) || undefined,
          });
        }
      } catch { /* le hook perf ne doit jamais casser la soumission de score */ }
      return rec;
    } catch { return null; }
  }
  async leaderboardList(leaderboardId, limit = 20) {
    if (!this.enabled) return [];
    try {
      const r = await this._client().listLeaderboardRecords(this.session || undefined, leaderboardId, undefined, limit);
      return (r && r.records) || [];
    } catch { return []; }
  }

  // ── Matchmaking (Bomberman) ──
  async matchmakerAdd(query = '*', minCount = 2, maxCount = 4) {
    if (!this.socket) await this._openSocket();
    if (!this.socket) return null;
    try { const t = await this.socket.addMatchmaker(query, minCount, maxCount); return t.ticket; } catch { return null; }
  }
  async matchmakerRemove(ticket) {
    if (!this.socket || !ticket) return;
    try { await this.socket.removeMatchmaker(ticket); } catch { /* */ }
  }
  onMatchmakerMatched(cb) { if (this.socket) this.socket.onmatchmakermatched = cb; }
  async joinMatch(matchIdOrToken) {
    // BUGFIX : ouvrir le socket temps réel AVANT de rejoindre (sinon renvoyait null →
    // faux message « compte invité ? » alors que le membre est bien connecté).
    if (!this.socket) await this._openSocket();
    if (!this.socket) return null;
    try { return await this.socket.joinMatch(matchIdOrToken); } catch { return null; }
  }
  async leaveMatch(matchId) { if (matchId) { if (!this.socket) return; try { await this.socket.leaveMatch(matchId); } catch { /* */ } } }
  onMatchData(cb) { if (this.socket) this.socket.onmatchdata = cb; }
  // Présences temps réel du match (natif Nakama) : cb({ match_id, joins:[], leaves:[] }).
  // C'est LE mécanisme correct pour voir les joueurs entrer/sortir d'un salon (pas onmatchdata).
  onMatchPresence(cb) { if (this.socket) this.socket.onmatchpresence = cb; }

  // ── Statut EN LIGNE des amis (présence + activité) ──
  // Met à jour NOTRE statut (activité) — visible par ceux qui nous suivent (followUsers).
  async updateStatus(status) {
    if (!this.socket) await this._openSocket();
    if (!this.socket) return;
    // TYPE D'APPAREIL injecté dans TOUT statut objet ({a,…}) → `d`:'m' (mobile/tactile) ou 'p' (PC). Point unique
    // (couvre tous les appelants). Les amis lisent `d` (deviceFromStatus) pour afficher 📱 / 💻 dans la liste.
    let payload = status;
    if (status && typeof status === 'object') { try { payload = { ...status, d: this._deviceCode() }; } catch (e) { payload = status; } }
    const s = typeof payload === 'string' ? payload : JSON.stringify(payload || {});
    this._lastStatus = s;   // mémorisé pour le keepalive/reconnexion (maintient la présence fraîche)
    try { await this.socket.updateStatus(s); } catch { /* */ }
  }
  _deviceCode() {
    try {
      const touch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
        || (window.matchMedia && window.matchMedia('(pointer:coarse)').matches) || (window.innerWidth <= 820);
      return touch ? 'm' : 'p';
    } catch (e) { return 'p'; }
  }
  // Suit des utilisateurs → renvoie les présences EN LIGNE parmi eux ({ user_id, username, status }).
  async followUsers(userIds) {
    const ids = (userIds || []).filter(Boolean);
    if (!ids.length) return [];
    if (!this.socket) await this._openSocket();
    if (!this.socket) return [];
    try { const r = await this.socket.followUsers(ids); return (r && r.presences) || []; } catch { return []; }
  }
  // Changements de statut temps réel des utilisateurs suivis : cb({ joins:[], leaves:[] }).
  // Multi-abonnés (page Amis + salon + notifieur global) → renvoie une fonction de désabonnement.
  onStatusPresence(cb) { this._statusCbs.add(cb); return () => this._statusCbs.delete(cb); }
  async sendMatchState(matchId, opCode, data) {
    if (!this.socket || !matchId) return;
    // IMPORTANT : on encode en Uint8Array (UTF-8) et PAS en string. Le SDK Nakama fait `btoa()` sur une
    // string → il LÈVE une exception sur tout caractère non-Latin1 (emoji 😀, > U+00FF) → le message n'est
    // JAMAIS envoyé (les accents FR ≤ 255 passaient, d'où « texte OK, emoji KO »). En Uint8Array il prend le
    // chemin base64-de-bytes, sûr pour tout Unicode. Le récepteur décode déjà via TextDecoder().decode(d.data).
    try { await this.socket.sendMatchState(matchId, opCode, new TextEncoder().encode(JSON.stringify(data || {}))); } catch { /* */ }
  }
}

// Singleton partagé dans toute l'app.
export const nakamaAuth = new NakamaAuth();
